import { z } from 'zod';
import { REVIEW_CYCLE_MONTHS_DEFAULT, REVIEW_SCORE_MAX, REVIEW_SCORE_MIN, REVIEW_STATUSES, WARNING_LEVELS } from '@bulk/types';
import { dateOnlySchema, idSchema, patchOf } from './common';

const localizedLabels = z.record(z.string(), z.string().trim().max(60));
const longText = z.string().trim().max(4000).nullable().optional();

export const reviewSettingsSchema = z.object({
  cycleMonths: z.number().int().min(1).max(24).default(REVIEW_CYCLE_MONTHS_DEFAULT).describe('Reviews fall due every this many months from each employee’s start date'),
});
export type ReviewSettings = z.infer<typeof reviewSettingsSchema>;

export const reviewCriterionSchema = z.object({
  id: idSchema,
  key: z.string(),
  label: z.string(),
  labelLocalized: z.record(z.string(), z.string()),
  sortOrder: z.number().int(),
  active: z.boolean(),
});
export type ReviewCriterionDto = z.infer<typeof reviewCriterionSchema>;

export const reviewSetupSchema = z.object({
  cycleMonths: z.number().int().describe('Reviews fall due every this many months from each employee’s start date'),
  criteria: z.array(reviewCriterionSchema),
});
export type ReviewSetupDto = z.infer<typeof reviewSetupSchema>;

export const updateReviewSetupSchema = z.object({
  cycleMonths: z.number().int().min(1).max(24).optional(),
  criteria: z
    .array(
      z.object({
        id: idSchema.optional().describe('An existing criterion; leave out to add one'),
        label: z.string().trim().min(1).max(60).describe('English label'),
        labelLocalized: localizedLabels.default({}),
        active: z.boolean().default(true).describe('Off hides it from new reviews; old reviews keep it'),
      }),
    )
    .max(20)
    .optional()
    .describe('The whole list in order; a criterion left out is switched off, never deleted'),
});
export type UpdateReviewSetupInput = z.infer<typeof updateReviewSetupSchema>;

const ratingsSchema = z.record(z.string().min(1).max(60), z.number().int().min(REVIEW_SCORE_MIN).max(REVIEW_SCORE_MAX)).describe('Score per criterion key, 1 to 5');

const reviewFields = z.object({
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  ratings: ratingsSchema.default({}),
  strengths: longText,
  improvements: longText,
  goals: longText,
});
export const createReviewSchema = reviewFields.refine((v) => v.periodEnd >= v.periodStart, { path: ['periodEnd'], message: 'The period cannot end before it starts' });
export type CreateReviewInput = z.infer<typeof createReviewSchema>;
export const updateReviewSchema = patchOf(reviewFields);
export type UpdateReviewInput = z.infer<typeof updateReviewSchema>;

export const acknowledgeSchema = z.object({ comment: z.string().trim().max(2000).nullable().optional().describe('The employee’s own words, kept beside the record') });
export type AcknowledgeInput = z.infer<typeof acknowledgeSchema>;

export const reviewRatingSchema = z.object({ key: z.string(), label: z.string(), labelLocalized: z.record(z.string(), z.string()), score: z.number().int() });

export const reviewSchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  employeeName: z.string(),
  reviewerUserId: idSchema.nullable(),
  reviewerName: z.string().nullable(),
  periodStart: z.string(),
  periodEnd: z.string(),
  status: z.enum(REVIEW_STATUSES),
  ratings: z.array(reviewRatingSchema),
  average: z.number().nullable(),
  strengths: z.string().nullable(),
  improvements: z.string().nullable(),
  goals: z.string().nullable(),
  employeeComment: z.string().nullable(),
  sharedAt: z.string().nullable(),
  acknowledgedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ReviewDto = z.infer<typeof reviewSchema>;

export const createWarningSchema = z.object({
  level: z.enum(WARNING_LEVELS),
  reason: z.string().trim().min(3).max(200),
  details: longText,
  expiresOn: dateOnlySchema.nullable().optional().describe('After this day the warning no longer counts; it stays on record'),
});
export type CreateWarningInput = z.infer<typeof createWarningSchema>;

export const warningSchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  employeeName: z.string(),
  issuedByName: z.string().nullable(),
  issuedAt: z.string(),
  level: z.enum(WARNING_LEVELS),
  reason: z.string(),
  details: z.string().nullable(),
  expiresOn: z.string().nullable(),
  expired: z.boolean(),
  acknowledgedAt: z.string().nullable(),
  employeeComment: z.string().nullable(),
});
export type WarningDto = z.infer<typeof warningSchema>;

export const reviewRecordsSchema = z.object({ employeeName: z.string(), reviews: z.array(reviewSchema), warnings: z.array(warningSchema) });
export type ReviewRecordsDto = z.infer<typeof reviewRecordsSchema>;

export const reviewDueQuerySchema = z.object({ days: z.coerce.number().int().min(1).max(365).default(30) });
export type ReviewDueQuery = z.infer<typeof reviewDueQuerySchema>;

export const reviewDueSchema = z.object({
  employeeId: idSchema,
  employeeName: z.string(),
  position: z.string().nullable(),
  dueOn: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  daysLeft: z.number().int().describe('Negative once overdue'),
  lastReviewedOn: z.string().nullable().describe('The end of the last shared period'),
  draftReviewId: idSchema.nullable(),
  openWarnings: z.number().int().describe('Warnings not expired'),
});
export type ReviewDueDto = z.infer<typeof reviewDueSchema>;

export const timelineItemSchema = z.object({
  kind: z.enum(['REVIEW', 'WARNING', 'AWARD', 'KUDO']),
  id: idSchema,
  date: z.string().describe('A calendar day or an instant'),
  title: z.string().nullable(),
  by: z.string().nullable(),
  status: z.enum(REVIEW_STATUSES).nullable(),
  level: z.enum(WARNING_LEVELS).nullable(),
  average: z.number().nullable(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
});
export type TimelineItemDto = z.infer<typeof timelineItemSchema>;
